import { useEffect, useRef } from "react";
import { SoftBox } from "../game/engine/roundedBoxes";
import * as THREE from "three";
import type { AvatarConfig } from "@shared/types";
import { buildRunnerCharacter } from "../game/engine/runnerCharacter";

interface Props {
  avatar: AvatarConfig;
  /** Glow color: gold for win, neutral otherwise. */
  flawless?: boolean;
}

/**
 * 3D victory podium using the same rounded bean language as the lobby/player.
 */
export function PodiumScene({ avatar, flawless }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 80);
    camera.position.set(4.2, 3.5, 6.4);
    camera.lookAt(0, 1.6, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 1.1));
    const sun = new THREE.DirectionalLight(0xffffff, 1.7);
    sun.position.set(4, 8, 7);
    sun.castShadow = true;
    scene.add(sun);
    const rimLight = new THREE.PointLight(flawless ? 0xffdf5a : 0xff2ca4, 2.5, 18);
    rimLight.position.set(-3.5, 3.8, 4);
    scene.add(rimLight);

    const podiumColor = flawless ? 0xffd93d : 0xff2ca4;
    const podium = new THREE.Mesh(
      new THREE.CylinderGeometry(2.05, 2.35, 0.72, 36),
      new THREE.MeshStandardMaterial({ color: podiumColor, metalness: 0.18, roughness: 0.36 }),
    );
    podium.position.y = 0.36;
    podium.receiveShadow = true;
    podium.castShadow = true;
    scene.add(podium);
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(2.15, 0.09, 10, 48),
      new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.28, roughness: 0.26 }),
    );
    rim.rotation.x = Math.PI / 2;
    rim.position.y = 0.75;
    scene.add(rim);

    // Shared runner geometry — identical to the in-game player so the podium
    // character matches whatever the kid is running with.
    const runnerParts = buildRunnerCharacter(avatar);
    const { group: runner, body, leftLeg, rightLeg } = runnerParts;
    runner.position.y = 0.62;
    scene.add(runner);

    const sparkles: THREE.Mesh[] = [];
    const sparkleColors = [0xff2ca4, 0xffd93d, 0x28d7ff, 0x5cff74, 0xffffff];
    for (let i = 0; i < 34; i++) {
      const star = new THREE.Mesh(
        new SoftBox(0.08, 0.08, 0.08),
        new THREE.MeshBasicMaterial({ color: sparkleColors[i % sparkleColors.length] }),
      );
      star.position.set((Math.random() - 0.5) * 6.6, 1.2 + Math.random() * 3.1, -1.2 + (Math.random() - 0.5) * 3.8);
      scene.add(star);
      sparkles.push(star);
    }

    // Resize
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    let raf = 0;
    let last = 0;
    const FRAME = 1000 / 30;
    const start = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (now - last < FRAME) return;
      last = now;
      const t = (now - start) * 0.001;

      const bounce = Math.abs(Math.sin(t * 3.4)) * 0.22;
      const sway = Math.sin(t * 3.4);
      const breath = Math.sin(t * 2.2) * 0.04;
      runner.position.y = 0.62 + bounce;
      body.rotation.y = Math.sin(t * 2.1) * 0.36;
      body.rotation.z = sway * 0.15;
      body.rotation.x = Math.abs(sway) * 0.05;
      body.scale.set(1 - breath * 0.18, 1 + breath, 1 - breath * 0.18);

      leftLeg.rotation.x = -sway * 0.36;
      rightLeg.rotation.x = sway * 0.36;
      leftLeg.rotation.z = -0.07 - Math.max(0, sway) * 0.16;
      rightLeg.rotation.z = 0.07 + Math.max(0, -sway) * 0.16;
      leftLeg.position.y = 0.58 + Math.max(0, sway) * 0.16;
      rightLeg.position.y = 0.58 + Math.max(0, -sway) * 0.16;

      for (let i = 0; i < sparkles.length; i++) {
        const star = sparkles[i]!;
        star.position.y += 0.012 + (i % 3) * 0.003;
        if (star.position.y > 4.7) star.position.y = 1.0;
        star.rotation.x += 0.08;
        star.rotation.y += 0.06;
      }

      const a = t * 0.23;
      camera.position.set(Math.sin(a) * 5.2, 3.3 + Math.sin(t * 1.1) * 0.18, Math.cos(a) * 5.2 + 1.1);
      camera.lookAt(0, 1.72, 0);

      podium.rotation.y = t * 0.18;
      rim.rotation.z = -t * 0.32;

      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      scene.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          const m = o.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else m.dispose();
        }
      });
    };
  }, [avatar, flawless]);

  return <canvas ref={ref} className="podium-scene" />;
}

